import { useState, useEffect } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import './BudgetStructure.css';
import IconButton from '@mui/material/IconButton';
import axiosInstance from '../../../Config/axiosInstance';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import EditNoteSharpIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';

function BudgetStructure() {
  const [canCreate, setCanCreate] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [deleteErrorMessage, setDeleteErrorMessage] = useState('');

  useEffect(() => {
    const requiredPath = "/project-budget";
    const storedPermissions = JSON.parse(localStorage.getItem("permissions"));
    const permissions = storedPermissions?.pages || [];


    const hasCreatePermission = permissions.some((page) => {
      const pagePath = new URL(page.url).pathname;
      return pagePath === requiredPath && page.can_create === true;
    });

    setCanCreate(hasCreatePermission);

    const hasEditPermission = permissions.some((page) => {
      const pagePath = new URL(page.url).pathname;
      return pagePath === requiredPath && page.can_edit === true;
    });

    setCanEdit(hasEditPermission);

    setCanDelete(permissions.some((page) => new URL(page.url).pathname === requiredPath && page.can_delete));
  }, []);

  const [rows, setRows] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axiosInstance.get('budget-structure/');
        const data = response.data.map((item) => ({
          id: item.id,
          codOferta: item.offer_code_details?.offer_code || ' ',
          projectCode: item.project_code_details?.project_code || '-',
          costId: item.cost_element_id,
          costDescription: item.cost_element_description,
        }));

        const sortedData = data.sort((a, b) => b.id - a.id);

        setRows(sortedData);
      } catch (error) {
        console.error('Eroare la obținerea datelor:', error.response ? error.response.data : error.message);
      }
    };

    fetchData();
  }, []);


  const handleAddClick = () => {
    navigate('/project-budget/budget-structure/add-budget-structure');
  };


  const handleEditClick = (row) => {
    navigate(`/project-budget/budget-structure/edit-budget-structure`, { state: { id: row.id } });
  };

  //Stergere
  const handleDeleteClick = (row) => {
    setItemToDelete(row);
    setOpenDeleteDialog(true);
  };
  const columns = [
    { field: 'codOferta', headerName: 'Offer Code', flex: 1, minWidth: 120 },
    { field: 'projectCode', headerName: 'Project Code', flex: 1, minWidth: 190 },
    { field: 'costId', headerName: 'Cost ID', flex: 1, minWidth: 110 },
    { field: 'costDescription', headerName: 'Cost Description', flex: 2, minWidth: 250 },

  ];
  if (canEdit || canDelete) {
    columns.push({
      field: 'action',
      headerName: 'Action',
      width: 100,
      renderCell: (params) => (
        <>
          {canEdit && (
            <IconButton onClick={() => handleEditClick(params.row)} sx={{ color: '#1976d2' }}>
              <EditNoteSharpIcon />
            </IconButton>
          )}
          {canDelete && (
            <IconButton onClick={() => handleDeleteClick(params.row)} sx={{ color: '#d32f2f' }}>
              <DeleteIcon />
            </IconButton>
          )}
        </>
      ),
      sortable: false,
      disableColumnMenu: true,
    });
  }

  return (
    <div className="budget-structure-page">
      {canCreate && (
        <div className="add-button-container">
          <Button
            variant="contained"
            onClick={handleAddClick}
            disabled={!canCreate}
            sx={{ textTransform: "none" }}
            style={{
              float: 'right',
              marginBottom: '15px',
            }}
          >
            <AddIcon />
            Add Budget Structure
          </Button>
        </div>
      )}
      <div className="table-container">
        <DataGrid
          id="budget-structure-table"
          rows={rows}
          columns={columns}
          pageSize={5}
          initialState={{
            sorting: {
              sortModel: [{ field: 'id', sort: 'desc' }]
            },
            pagination: {
              paginationModel: {
                pageSize: 10,
              },
            },
          }}
          disableRowSelectionOnClick={true}
          sx={{
            "& .MuiDataGrid-cell:focus": {
              outline: "none",
            },
            "& .MuiDataGrid-cell:focus-within": {
              outline: "none",
            },
            "& .Mui-selected:hover": {
              backgroundColor: "transparent !important"
            },
          }}
          pageSizeOptions={[10, 20, 25]}
          rowHeight={40}
        />

      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this record?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenDeleteDialog(false)}
            sx={{
              backgroundColor: '#e0e0e0',
              color: '#424242',
              '&:hover': {
                backgroundColor: '#bdbdbd',
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              try {
                await axiosInstance.delete(`/budget-structure/${itemToDelete.id}/`);
                setRows(prevRows => prevRows.filter(r => r.id !== itemToDelete.id));
                setOpenDeleteDialog(false);
              } catch (error) {
                const message =
                  error.response?.data?.error ||
                  error.response?.data?.detail ||
                  "An unexpected error occurred while deleting.";
                setDeleteErrorMessage(message);
                setOpenDeleteDialog(false);  
                setErrorDialogOpen(true);    
              }
            }}
            sx={{
              backgroundColor: '#d32f2f',
              color: 'white',
              '&:hover': {
                backgroundColor: '#b71c1c',
              }
            }}
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog pentru eroare */}
      <Dialog
        open={errorDialogOpen}
        onClose={() => setErrorDialogOpen(false)}
        aria-labelledby="error-dialog-title"
        aria-describedby="error-dialog-description"
      >
        <DialogTitle id="error-dialog-title" style={{ color: 'red' }}>Delete Failed</DialogTitle>
        <DialogContent>
          <DialogContentText id="error-dialog-description">
            {deleteErrorMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setErrorDialogOpen(false)}
            sx={{
              backgroundColor: '#e0e0e0',
              color: '#424242',
              '&:hover': {
                backgroundColor: '#bdbdbd',
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default BudgetStructure;
